import {LazyQueryHookExecOptions, useLazyQuery} from '@apollo/client';
import {
    CursorBasedPaginationRequest,
    CursorBasedPaginationResponse,
    OffsetBasedPaginationRequest,
    OffsetBasedPaginationResponse,
} from 'api/common';
import {DocumentNode} from 'graphql';
import {useCallback, useEffect, useRef, useState} from 'react';

export const useDebounceEffect = (callback: () => void, dependencies: any[], delay = 500) => {
    const debounceRef = useRef<NodeJS.Timeout | null>(null);
    useEffect(() => {
        debounceRef.current = setTimeout(callback, delay);
        return () => {
            if (debounceRef.current) {
                clearTimeout(debounceRef.current);
            }
        };
    }, dependencies);
};

export const useCursorPaginationQuery = <
    TItem extends {id?: string}, // assumes each item has a unique id
    TResponse extends Record<TKey, CursorBasedPaginationResponse<TItem>>,
    TVariables extends CursorBasedPaginationRequest,
    TKey extends keyof TResponse,
>(
    query: DocumentNode,
    responseKey: TKey,
    fetchPolicy: 'network-only' | 'cache-first' | 'no-cache' = 'network-only',
) => {
    const [state, setState] = useState<CursorBasedPaginationResponse<TItem> | undefined>();

    const [fetchQuery, {loading, data}] = useLazyQuery<TResponse, TVariables>(query, {
        fetchPolicy,
    });

    const fetchPaginated = useCallback(
        (options?: Partial<LazyQueryHookExecOptions<TResponse, TVariables>>) => {
            const after = options?.variables?.after;
            console.log('after', after);

            if (!after) {
                setState(undefined);
            }
            fetchQuery(options);
        },
        [fetchQuery],
    );

    useEffect(() => {
        if (data?.[responseKey]) {
            const newData = data[responseKey];
            setState(prev => {
                console.log('prev', prev);
                console.log('newData', newData);

                return {
                    ...newData,
                    nodes: [...(prev?.nodes ?? []), ...newData.nodes],
                    edges: [...(prev?.edges ?? []), ...newData.edges],
                };
            });
        }
    }, [data, responseKey]);

    // 🔼 Add or Update an item
    const upsertItemState = useCallback((item: TItem, position: 'start' | 'end' = 'start') => {
        setState(prev => {
            if (!prev) return undefined;

            const existsIndex = prev.nodes.findIndex(n => n.id === item.id);
            let updatedNodes: TItem[];
            if (existsIndex !== -1) {
                // Update existing
                updatedNodes = [...prev.nodes];
                updatedNodes[existsIndex] = item;
            } else {
                // Add new
                updatedNodes = position === 'start' ? [item, ...prev.nodes] : [...prev.nodes, item];
            }

            return {
                ...prev,
                nodes: updatedNodes,
                edges: updatedNodes.map(node => ({
                    cursor: `cursor-${node.id}`, // or compute a real cursor if needed
                    node,
                })),
            };
        });
    }, []);

    // 🔼 Remove an item by id
    const removeItemState = useCallback((id: string) => {
        setState(prev => {
            if (!prev) return undefined;

            const updatedNodes = prev.nodes.filter(n => n.id !== id);
            return {
                ...prev,
                nodes: updatedNodes,
                edges: updatedNodes.map(node => ({
                    cursor: `cursor-${node.id}`, // or compute a real cursor if needed
                    node,
                })),
            };
        });
    }, []);

    return {
        data: state,
        isLoading: loading,
        fetchPaginated,
        upsertItemState,
        removeItemState,
    };
};

export const useOffsetPaginationQuery = <
    TItem,
    TResponse extends Record<TKey, OffsetBasedPaginationResponse<TItem>>,
    TVariables extends OffsetBasedPaginationRequest,
    TKey extends keyof TResponse,
>(
    query: DocumentNode,
    responseKey: TKey,
    fetchPolicy: 'network-only' | 'cache-first' | 'no-cache' = 'network-only',
) => {
    const [state, setState] = useState<OffsetBasedPaginationResponse<TItem> | undefined>();

    const [fetchQuery, {loading, data}] = useLazyQuery<TResponse, TVariables>(query, {
        fetchPolicy,
    });

    const fetchPaginated = useCallback(
        (options?: Partial<LazyQueryHookExecOptions<TResponse, TVariables>>) => {
            fetchQuery(options);
        },
        [fetchQuery],
    );

    useEffect(() => {
        if (data?.[responseKey]) {
            const newData = data[responseKey];
            // Overwrite items (not append)
            setState({
                ...newData,
                items: newData.items,
            });
        }
    }, [data, responseKey]);

    const upsertItemState = useCallback((item: TItem, position: 'start' | 'end' = 'start') => {
        setState(prev => {
            if (!prev) return undefined;

            const id = (item as any).id;
            const existingIndex = prev.items.findIndex((n: any) => n.id === id);

            let updatedItems: TItem[];
            if (existingIndex !== -1) {
                updatedItems = [...prev.items];
                updatedItems[existingIndex] = item;
            } else {
                updatedItems = position === 'start' ? [item, ...prev.items] : [...prev.items, item];
            }

            return {
                ...prev,
                items: updatedItems,
            };
        });
    }, []);

    return {
        data: state,
        isLoading: loading,
        fetchPaginated,
        upsertItemState,
    };
};
